"use client";

import { useState } from "react";
import { TRUCK_SPECS } from "@/lib/calculators/truck-loading-merged/truck-specs";
import type { TruckSpec } from "@/lib/calculators/truck-loading-merged/types";
import { TruckIcon } from "../shared/truck-icons";
import { SelectableCard } from "@/components/ui/selectable-card";
import {
  SegmentedControl,
  type SegmentedControlOption,
} from "@/components/ui/segmented-control";

type TruckGroup = TruckSpec["group"];

const TRUCK_GROUP_OPTIONS: Array<SegmentedControlOption<TruckGroup>> = [
  { value: "popular", label: "Most popular" },
  { value: "other", label: "Other options" },
];

function TruckCard({
  truck,
  selected,
  onSelect,
}: {
  truck: TruckSpec;
  selected: boolean;
  onSelect: (truck: TruckSpec) => void;
}) {
  return (
    <SelectableCard
      size="compact"
      isSelected={selected}
      aria-pressed={selected}
      onClick={() => onSelect(truck)}
      className="h-full w-full"
    >
      <div
        className={`mb-2 ${selected ? "text-action" : "text-muted-foreground group-hover:text-action"}`}
      >
        <TruckIcon truckId={truck.id} />
      </div>
      <div
        className={`text-sm font-semibold leading-tight mb-1.5 text-center ${selected ? "text-action" : "text-foreground"}`}
      >
        {truck.label}
      </div>
      <div
        className={`text-xs space-y-0.5 ${selected ? "text-action" : "text-muted-foreground"}`}
      >
        <div>{truck.maxWeightLbs.toLocaleString()} lbs</div>
        <div>
          {truck.lengthIn}&quot; x {truck.widthIn}&quot; x {truck.heightIn}
          &quot;
        </div>
        {truck.skidSpots && <div>{truck.skidSpots} skid spots</div>}
      </div>
    </SelectableCard>
  );
}

interface TruckSelectorProps {
  selectedTruckId: string;
  onSelect: (truck: TruckSpec) => void;
}

export function TruckSelector({
  selectedTruckId,
  onSelect,
}: TruckSelectorProps) {
  const [tab, setTab] = useState<TruckGroup>("popular");

  const trucks = TRUCK_SPECS.filter((truck) => truck.group === tab);

  return (
    <section className="@container/trucks">
      <div className="mb-4">
        <SegmentedControl
          aria-label="Truck group"
          value={tab}
          onValueChange={setTab}
          options={TRUCK_GROUP_OPTIONS}
        />
      </div>

      <ul className="grid grid-cols-2 gap-3 @lg/trucks:grid-cols-3 @3xl/trucks:grid-cols-4">
        {trucks.map((truck) => (
          <li key={truck.id}>
            <TruckCard
              truck={truck}
              selected={selectedTruckId === truck.id}
              onSelect={onSelect}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
